import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { HiOutlineArrowNarrowRight } from "react-icons/hi";
import { Infertility } from "../../data/FemaleInfertility";
import img from "../../assets/images/Home/slide2.jpeg";

// --- Global Theme Settings (Logo Based & Poppins Font) ---
const COLORS = {
  accent: "#D7A19E", // Logo Muted Rose
  dark: "#546E7A", // Logo Dark Slate Blue
  textLight: "#4A5A64", // Soft Blue-Grey for readability
  overlay: "rgba(0, 0, 0, 0.4)", // Thoda dark overlay taaki white text dikhe
  fontFamily: "'Poppins', sans-serif",
};

const ServiceCard = ({ item, index, scrollYProgress }) => {
  const xPos = useTransform(scrollYProgress, [0, 0.4], [index * 10 - 20, 0]);
  const yPos = useTransform(scrollYProgress, [0, 0.4], [index * -5, 0]);
  const rotate = useTransform(
    scrollYProgress,
    [0, 0.4],
    [index % 2 === 0 ? 5 : -5, 0],
  );
  const opacity = useTransform(scrollYProgress, [0, 0.2], [0, 1]);
  const scale = useTransform(scrollYProgress, [0, 0.4], [0.9, 1]);

  return (
    <motion.div
      style={{
        x: xPos,
        y: yPos,
        rotateZ: rotate,
        opacity,
        scale,
        fontFamily: COLORS.fontFamily,
      }}
      className="group bg-white rounded-[2.5rem] shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 p-10 flex flex-col"
    >
      {/* Accent Line */}
      <div
        className="w-12 h-1 rounded-full mb-6 mx-auto"
        style={{ backgroundColor: COLORS.accent }}
      />

      {/* Title */}
      <h4
        className="text-2xl font-semibold mb-4 text-center"
        style={{ color: COLORS.dark }}
      >
        {item.title}
      </h4>

      {/* Description */}
      <p
        className="text-sm leading-relaxed mb-8 text-center"
        style={{ color: COLORS.textLight }}
      >
        {item.shortDescription}
      </p>

      {/* Button */}
      <Link
        to={`/female-infertility/${item.slug}`}
        className="group/btn relative flex items-center justify-center gap-3 px-6 py-3 rounded-2xl font-bold text-xs uppercase tracking-widest transition-all duration-300 overflow-hidden mt-auto"
        style={{
          color: COLORS.accent,
          border: `1px solid ${COLORS.accent}`,
        }}
      >
        <span className="relative z-10 transition-colors duration-300 group-hover/btn:text-white">
          Read More
        </span>

        <motion.span className="relative z-10 transition-transform duration-300 group-hover/btn:translate-x-2 group-hover/btn:text-white">
          <HiOutlineArrowNarrowRight size={18} />
        </motion.span>

        <div
          className="absolute inset-0 translate-y-full group-hover/btn:translate-y-0 transition-transform duration-300 ease-out z-0"
          style={{ backgroundColor: COLORS.accent }}
        />
      </Link>
    </motion.div>
  );
};

const FemaleInfertility = () => {
  const containerRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"],
  });

  const infertilityServices = Infertility.filter(
    (item) => item.category === "female-infertility",
  );

  return (
    <section
      ref={containerRef}
      className="relative py-24 px-6 flex flex-col items-center overflow-hidden"
      style={{
        backgroundImage: `url(${img})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        fontFamily: COLORS.fontFamily,
      }}
    >
      {/* Dark Overlay for better text contrast */}
      <div
        className="absolute inset-0"
        style={{ backgroundColor: COLORS.overlay }}
      ></div>

      <div className="relative z-10 w-full max-w-7xl">
        {/* Heading Section */}
        <div className="text-center mb-20">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white tracking-tight">
            Female Infertility
          </h2>
          <p className="text-lg text-gray-100 max-w-3xl mx-auto leading-relaxed">
            Our infertility treatments combine advanced medical techniques with
            personalized care to help women achieve their dream of motherhood.
            We offer safe, effective, and compassionate solutions.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {infertilityServices.map((item, i) => (
            <ServiceCard
              key={item.id}
              item={item}
              index={i}
              scrollYProgress={scrollYProgress}
            />
          ))}
        </div>
      </div>

      <style
        dangerouslySetInnerHTML={{
          __html: `.group\\/btn:hover span { color: white !important; }`,
        }}
      />
    </section>
  );
};

export default FemaleInfertility;
